// https://dashboard.unlayer.com/projects/169512/settings/embed

declare let unlayer: any;

interface UnlayerExport {
    design: string;
    html: string;
}

export class UnlayerEditor {

    private readonly UNLAYER_ID = "unlayer-editor";
    private readonly NOTIFY_METHOD = "NotifyUnlayerReady";
    private readonly IMAGE_UPLOADED_METHOD = "UnlayerImageUploaded";
    private readonly LOCKED_VERSION = "stable"; // 1.309.4 as stable at 28-11-2025

    public init(
        projectId: string,
        dotNetRef: any,
        baseUrl: string,
        tenantId: number): void {
        try {
            this.initializeEditor({projectId, dotNetRef, baseUrl, tenantId});
        } catch (e) {
            console.error("[Unlayer]: Failed to initialize editor.");
        }
    }

    public load(
        projectId: string,
        dotNetRef: any,
        baseUrl: string,
        tenantId: number,
        design: string,
        mergeTags: any): void {
        try {
            this.initializeEditor({projectId, dotNetRef, baseUrl, tenantId, mergeTags});

            unlayer.addEventListener("editor:ready", () => {
                unlayer.loadDesign(JSON.parse(design));
            });

            unlayer.addEventListener("design:loaded", () => {
                dotNetRef.invokeMethodAsync(this.NOTIFY_METHOD);
            });
        } catch (e) {
            console.error("[Unlayer]: Failed to load template.");
        }
    }

    public async export(): Promise<UnlayerExport | null> {
        try {
            const finalData = await new Promise<UnlayerExport>((resolve, reject) => {
                unlayer.exportHtml((data: any) => {
                    try {
                        resolve({
                            design: JSON.stringify(data.design) as string,
                            html: data.html as string
                        });
                    } catch (error) {
                        reject(error);
                    }
                });
            });

            return finalData;
        } catch (e) {
            console.error("[Unlayer]: Failed to export.");
            return null;
        }
    }

    public setMergeTags(tags: any): void {
        try {
            unlayer.setMergeTags(tags);
        } catch (e) {
            console.error("[Unlayer]: Failed to set merge tags.");
        }
    }

    //Helper methods

    private initializeEditor(config: {
        projectId: string,
        dotNetRef: any,
        baseUrl: string,
        tenantId: number,
        mergeTags?: any
    }) {
        const {projectId, dotNetRef, baseUrl, tenantId, mergeTags} = config;

        unlayer.destroy();

        unlayer.init({
            id: this.UNLAYER_ID,
            projectId,
            version: this.LOCKED_VERSION,
            mergeTags
        });

        unlayer.registerCallback("image", async (file: any, done: any) =>
            this.fileUploadCallback(dotNetRef, baseUrl, file, done, tenantId)
        );

        unlayer.addEventListener("editor:ready", () => {
            dotNetRef.invokeMethodAsync(this.NOTIFY_METHOD);
        });
    }

    private async fileUploadCallback(
        dotNetRef: any,
        baseUrl: string,
        file: any,
        done: any,
        tenantId: number
    ): Promise<void> {
        const formData = new FormData();
        formData.append("file", file.attachments[0]);
        formData.append("url", baseUrl);

        try {
            const uploadRequestUrl = `${baseUrl}/api/unlayer/image/${tenantId}`;
            const response = await fetch(uploadRequestUrl, {
                method: "POST",
                headers: {Accept: "application/json"},
                body: formData
            });

            if (!response.ok) {
                var errorMsg = `[Unlayer] Image upload response not ok: ${response.status} - ${response.statusText}`;
                await dotNetRef.invokeMethodAsync(this.IMAGE_UPLOADED_METHOD, false, errorMsg);
                return;
            }

            const data = await response.json();
            await dotNetRef.invokeMethodAsync(this.IMAGE_UPLOADED_METHOD, true, data);

            const uploadResponseUrl = `${baseUrl}/api/unlayer/image/${data}`;
            done({progress: 100, url: uploadResponseUrl});
        } catch (error: any) {
            console.error("[Unlayer]: Failed to upload image.");
            await dotNetRef.invokeMethodAsync(this.IMAGE_UPLOADED_METHOD, false, error.message);
        }
    }
}

// Register globally
(window as any)["Unlayer"] = new UnlayerEditor();
